<template>
  <div class="mk-page">
    <MarketplaceHeader />

    <section class="container py-4" style="max-width: 860px;">
      <h1 class="h4 font-weight-bold">Sell on Claimify Marketplace</h1>
      <p class="text-muted">
        Create a vendor account to list hospital equipment and supplies for health facilities. You can build your
        catalogue straight away; products go live once your business is approved.
      </p>

      <form class="mk-panel" @submit.prevent="submit" novalidate>
        <h6 class="mk-section">Business</h6>
        <div class="form-row">
          <div class="form-group col-md-6">
            <label for="vs-business" class="mk-label">Business name *</label>
            <input id="vs-business" v-model="form.business_name" class="form-control" required autocomplete="organization" />
          </div>
          <div class="form-group col-md-6">
            <label for="vs-rc" class="mk-label">CAC / RC number</label>
            <input id="vs-rc" v-model="form.rc_number" class="form-control" />
          </div>
          <div class="form-group col-md-6">
            <label for="vs-bemail" class="mk-label">Business email</label>
            <input id="vs-bemail" v-model="form.business_email" type="email" class="form-control" placeholder="Defaults to your email" />
          </div>
          <div class="form-group col-md-6">
            <label for="vs-bphone" class="mk-label">Business phone</label>
            <input v-phone id="vs-bphone" v-model="form.business_phone" class="form-control" placeholder="Defaults to your phone" />
          </div>
          <div class="form-group col-md-8">
            <label for="vs-address" class="mk-label">Address *</label>
            <input id="vs-address" v-model="form.address" class="form-control" required autocomplete="street-address" />
          </div>
          <div class="form-group col-md-4">
            <label for="vs-state" class="mk-label">State *</label>
            <select id="vs-state" v-model="form.state" class="form-control" required>
              <option value="">Select state…</option>
              <option v-for="s in states" :key="s" :value="s">{{ s }}</option>
            </select>
          </div>
          <div class="form-group col-md-6">
            <label for="vs-website" class="mk-label">Website</label>
            <input id="vs-website" v-model="form.website" type="url" class="form-control" placeholder="https://" />
          </div>
          <div class="form-group col-md-6">
            <label for="vs-agency" class="mk-label">Also sell to an agency's facilities</label>
            <select id="vs-agency" v-model="form.agency_id" class="form-control">
              <option value="">Public marketplace only</option>
              <option v-for="a in agencies" :key="a.agency_id" :value="a.agency_id">{{ a.name }}</option>
            </select>
          </div>
          <div class="form-group col-12">
            <label for="vs-desc" class="mk-label">What do you sell?</label>
            <textarea id="vs-desc" v-model="form.description" rows="3" class="form-control"
              placeholder="e.g. Patient monitors, surgical consumables, laboratory reagents…"></textarea>
          </div>
        </div>

        <h6 class="mk-section mt-2">Your login (vendor admin)</h6>
        <div class="form-row">
          <div class="form-group col-md-6">
            <label for="vs-first" class="mk-label">First name *</label>
            <input id="vs-first" v-model="form.firstname" class="form-control" required autocomplete="given-name" />
          </div>
          <div class="form-group col-md-6">
            <label for="vs-last" class="mk-label">Last name *</label>
            <input id="vs-last" v-model="form.lastname" class="form-control" required autocomplete="family-name" />
          </div>
          <div class="form-group col-md-6">
            <label for="vs-email" class="mk-label">Email *</label>
            <input id="vs-email" v-model="form.email" type="email" class="form-control" required autocomplete="email" />
          </div>
          <div class="form-group col-md-6">
            <label for="vs-phone" class="mk-label">Phone *</label>
            <input v-phone id="vs-phone" v-model="form.phone_number" class="form-control" required autocomplete="tel" />
          </div>
          <div class="form-group col-md-6">
            <label for="vs-pass" class="mk-label">Password * <span class="text-muted small">(8+ characters)</span></label>
            <input id="vs-pass" v-model="form.password" type="password" class="form-control" required autocomplete="new-password" />
          </div>
          <div class="form-group col-md-6">
            <label for="vs-pass2" class="mk-label">Confirm password *</label>
            <input id="vs-pass2" v-model="form.password_confirmation" type="password" class="form-control" required autocomplete="new-password" />
          </div>
        </div>

        <div v-if="errors.length" class="alert alert-danger small py-2">
          <div v-for="e in errors" :key="e">{{ e }}</div>
        </div>

        <button type="submit" class="btn mk-btn btn-block" :disabled="saving">
          <span v-if="saving" class="spinner-border spinner-border-sm mr-2" role="status"></span>
          Create vendor account
        </button>
        <p class="small text-muted text-center mt-3 mb-0">
          Already registered? <router-link to="/">Sign in</router-link>
        </p>
      </form>
    </section>
  </div>
</template>

<script>
import MarketplaceHeader from "./MarketplaceHeader.vue";
import AgenciesJson from "@/json/agencies.json";

const STATES = ["Abia", "Adamawa", "Akwa Ibom", "Anambra", "Bauchi", "Bayelsa", "Benue", "Borno", "Cross River", "Delta",
  "Ebonyi", "Edo", "Ekiti", "Enugu", "FCT", "Gombe", "Imo", "Jigawa", "Kaduna", "Kano", "Katsina", "Kebbi", "Kogi", "Kwara",
  "Lagos", "Nasarawa", "Niger", "Ogun", "Ondo", "Osun", "Oyo", "Plateau", "Rivers", "Sokoto", "Taraba", "Yobe", "Zamfara"];

export default {
  components: { MarketplaceHeader },
  data() {
    return {
      states: STATES,
      agencies: AgenciesJson,
      saving: false,
      errors: [],
      form: {
        business_name: "", rc_number: "", business_email: "", business_phone: "", address: "", state: "",
        website: "", agency_id: "", description: "",
        firstname: "", lastname: "", email: "", phone_number: "", password: "", password_confirmation: "",
      },
    };
  },
  methods: {
    submit() {
      this.errors = [];
      if (this.form.password !== this.form.password_confirmation) {
        this.errors = ["The passwords do not match."];
        return;
      }
      this.saving = true;
      const payload = Object.fromEntries(Object.entries(this.form).filter(([, v]) => v !== ""));
      this.axios
        .post("/api/v1/auth/vendor/register", payload)
        .then((res) => {
          // Same session keys as Login.vue; Pusher.vue routes to the vendor dashboard and reloads.
          localStorage.setItem("jwt", res.data.token);
          localStorage.setItem("user", JSON.stringify(res.data.user));
          localStorage.setItem("agency", JSON.stringify(res.data.agency));
          this.$router.push("/pusher");
        })
        .catch((err) => {
          const data = (err.response && err.response.data) || {};
          this.errors = data.errors ? Object.values(data.errors).flat() : [data.message || "Registration failed."];
        })
        .finally(() => (this.saving = false));
    },
  },
};
</script>

<style scoped>
.mk-page { background: #f1f5f9; min-height: 100vh; }
.mk-panel { background: #fff; border-radius: 12px; padding: 22px; box-shadow: 0 1px 4px rgba(0,0,0,.05); }
.mk-section { font-size: .75rem; text-transform: uppercase; letter-spacing: .05em; color: #64748b; font-weight: 700; margin-bottom: 12px; }
.mk-label { font-weight: 600; font-size: .85rem; color: #374151; }
.mk-btn { background: #1e3a5f; color: #fff; border-radius: 8px; font-weight: 600; padding: 10px; }
.mk-btn:hover { opacity: .9; color: #fff; }
</style>
